// Aktivitāšu kārtis: ietekme atkarīga no stāvokļa izspēles brīdī.

import { CAP, EKINDS, EMAX, KINDS, LIMITS, RAR } from './constants';
import { DAY } from './energy';
import { clamp, fmt2, round } from './format';
import type { Index } from './tree';
import type { CapacityTier, Card, CardEffect, CardSort, CardRule, CardStack, JournalEntry, Kind, NodeEnergy, Rarity, Role, ShieldKind } from './types';

/** Līmenis (vienībās) un modelis izspēles brīdī; `cap` — ietilpība (noklusējumā 100). */
export interface EnergyContext {
  L: number;
  M: { remain: Record<Kind, number>; cap?: number };
}

/** Cik reizes kārts izspēlēta pēdējās `days` dienās (neskaitot pašreizējo izspēli). */
export type PlayCounter = (days: number) => number;

/** Krāšanās sliekšņa stāvoklis: `have` — izspēles logā, ieskaitot šo. */
export interface StackState {
  i: number;
  have: number;
  need: number;
  days: number;
  active: boolean;
}

export interface Outcome {
  L: number;
  Lafter: number;
  energyDelta: number;
  /** Pamata enerģija (kārts × nosacījumi × noturība) bez krāšanās bonusa. */
  baseEnergy: number;
  /** Aktīvo krāšanās bonusu enerģija. */
  stackEnergy: number;
  effects: CardEffect[];
  active: number[];
  stacks: StackState[];
  eMul: number;
  aMul: number;
  res: number;
}

/** Izspēļu skaitītājs no žurnāla (pārlūkā — no ielādētā žurnāla, serverī — no datubāzes). */
export function playCounter(journal: Pick<JournalEntry, 'cardId' | 'ts'>[], cardId: string | undefined, now: number): PlayCounter {
  if (!cardId) return () => 0;
  const ts = journal.filter((j) => j.cardId === cardId && j.ts <= now).map((j) => j.ts);
  return (days) => ts.filter((t) => t > now - days * DAY).length;
}

/**
 * Aprēķina kārts ietekmi pašreizējā stāvoklī: nosacījumi reizinās, noturība samazina patēriņu,
 * krāšanās bonusi (ja kārts bieži izspēlēta) tiek pieskaitīti klāt bez reizināšanas.
 */
export function cardOutcome(card: Card, idx: Index, en: EnergyContext | null, prior?: PlayCounter): Outcome {
  const L = en ? en.L : 50;
  const active: number[] = [];
  let eMul = 1;
  let aMul = 1;
  card.rules.forEach((r, i) => {
    const v = r.metric === 'energy' ? L : idx.node.has(r.metric) ? idx.val.get(r.metric)! : null;
    if (v == null) return;
    if (r.op === 'lt' ? v < r.value : v > r.value) {
      eMul *= r.energyMul;
      aMul *= r.areaMul;
      active.push(i);
    }
  });
  const res = card.energy < 0 && en ? en.M.remain[card.kind] || 1 : 1;
  const baseEnergy = card.energy * eMul * res;
  const effects = card.effects
    .filter((e) => idx.node.has(e.target))
    .map((e) => ({ target: e.target, delta: e.delta * aMul }));

  const stacks: StackState[] = (card.stacks || []).map((s, i) => {
    const have = (prior ? prior(s.days) : 0) + 1;
    return { i, have, need: s.count, days: s.days, active: have >= s.count };
  });
  let stackEnergy = 0;
  for (const st of stacks) {
    if (!st.active) continue;
    const s = card.stacks[st.i];
    stackEnergy += s.energy;
    for (const e of s.effects) {
      if (!idx.node.has(e.target)) continue;
      const same = effects.find((x) => x.target === e.target);
      if (same) same.delta += e.delta;
      else effects.push({ target: e.target, delta: e.delta });
    }
  }
  const energyDelta = baseEnergy + stackEnergy;
  const cap = en?.M.cap ?? 100;
  return { L, Lafter: clamp(L + energyDelta, 0, cap), energyDelta, baseEnergy, stackEnergy, effects, active, stacks, eMul, aMul, res };
}

/**
 * Piemēro ietekmes lapu jomām (virsjomas izmaiņa tiek pievienota katrai tās apakšjomai).
 * Atgriež jaunās vērtības un faktiski piemērotās izmaiņas (pēc 0–10 robežām) atsaukšanai.
 */
export function applyEffects(idx: Index, effects: CardEffect[]) {
  const values = new Map<string, number>();
  const cur = (id: string) => values.get(id) ?? Number(idx.node.get(id)!.value);
  effects.forEach((e) =>
    (idx.leaves.get(e.target) || []).forEach((l) => {
      values.set(l, clamp(round(cur(l) + e.delta), 0, 10));
    })
  );
  const applied: { id: string; d: number }[] = [];
  values.forEach((v, id) => {
    const d = round(v - Number(idx.node.get(id)!.value));
    if (d) applied.push({ id, d });
  });
  return { values, applied };
}

export const metricName = (idx: Index, m: string) =>
  m === 'energy' ? 'enerģija' : idx.node.has(m) ? idx.node.get(m)!.name : '(dzēsta joma)';

/** Nosacījuma teksts (bez HTML): “Ja enerģija < 30: enerģija ×1,5, ietekme ×0,5” (enerģija — vienībās). */
export function ruleText(r: CardRule, idx: Index): string {
  const parts: string[] = [];
  if (r.energyMul !== 1) parts.push('enerģija ×' + fmt2(r.energyMul));
  if (r.areaMul !== 1) parts.push('ietekme ×' + fmt2(r.areaMul));
  return (
    'Ja ' + metricName(idx, r.metric) + ' ' + (r.op === 'lt' ? '<' : '>') + ' ' + fmt2(r.value) +
    ': ' + (parts.join(', ') || 'bez izmaiņām')
  );
}

/** Krāšanās sliekšņa nosacījums: “≥3× 10 d.” */
export const stackCondition = (s: Pick<CardStack, 'count' | 'days'>) => `≥${s.count}× ${s.days} ${s.days === 1 ? 'dienā' : 'dienās'}`;

/** Krāšanās bonusa teksts (bez HTML): “Ieradums (≥3× 7 dienās): enerģija +5, Veselība +0,2”. */
export function stackText(s: CardStack, idx: Index): string {
  const parts: string[] = [];
  if (s.energy) parts.push('enerģija ' + (s.energy > 0 ? '+' : '−') + Math.abs(s.energy));
  for (const e of s.effects) parts.push(metricName(idx, e.target) + ' ' + (e.delta >= 0 ? '+' : '−') + fmt2(Math.abs(e.delta)));
  const head = s.label ? `${s.label} (${stackCondition(s)})` : `Ja ${stackCondition(s)}`;
  return head + ': ' + (parts.join(', ') || 'bez efekta');
}

const collator = new Intl.Collator('lv', { sensitivity: 'base', numeric: true });
const byName = (a: Pick<Card, 'name'>, b: Pick<Card, 'name'>) => collator.compare(a.name, b.name);
const added = (c: Pick<Card, 'createdAt' | 'sortOrder'>) => c.createdAt ?? 0;

/**
 * Sakārto kārtis klājam (atgriež jaunu masīvu):
 * `added-desc` / `added-asc` — pēc pievienošanas laika (vienādam laikam — pēc secības klājā);
 * `name` — alfabētiski (latviešu valodas kārtība, cipari pēc vērtības);
 * `category` — pēc virsjomas secības uz apļa, bez kategorijas — beigās; grupā — alfabētiski.
 */
export function sortCards<T extends Pick<Card, 'name' | 'typeId' | 'sortOrder' | 'createdAt'>>(cards: T[], idx: Index, mode: CardSort): T[] {
  const list = [...cards];
  if (mode === 'name') return list.sort((a, b) => byName(a, b) || a.sortOrder - b.sortOrder);
  if (mode === 'category') {
    const rank = new Map(idx.tops.map((t, i) => [t, i]));
    const r = (c: T) => (c.typeId && rank.has(c.typeId) ? rank.get(c.typeId)! : idx.tops.length);
    return list.sort((a, b) => r(a) - r(b) || byName(a, b) || a.sortOrder - b.sortOrder);
  }
  const dir = mode === 'added-asc' ? 1 : -1;
  return list.sort((a, b) => dir * (added(a) - added(b) || a.sortOrder - b.sortOrder));
}

export const cardColor = (card: Pick<Card, 'typeId'>, idx: Index) =>
  card.typeId && idx.node.has(card.typeId) ? idx.color(card.typeId) : '#7d7466';

export const cardTypeName = (card: Pick<Card, 'typeId'>, idx: Index) =>
  card.typeId && idx.node.has(card.typeId) ? idx.node.get(card.typeId)!.name : 'Vispārēja';

export const cardKindText = (card: Pick<Card, 'energy' | 'kind'>) =>
  card.energy < 0 ? EKINDS[card.kind] + ' slodze' : card.energy > 0 ? 'atjaunojoša' : 'neitrāla';

// ---------- sanitizācija (izmanto serveris pirms saglabāšanas un imports) ----------

const num = (v: unknown, a: number, b: number, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? clamp(n, a, b) : d;
};

export function sanitizeCardFields(
  c: Partial<Card>,
  nodeIds: Set<string>
): Omit<Card, 'id' | 'image' | 'sortOrder'> {
  const rarity: Rarity = c.rarity && RAR[c.rarity] ? c.rarity : 'c';
  const kind: Kind = c.kind && (KINDS as string[]).includes(c.kind) ? c.kind : 'fiz';
  return {
    name: String(c.name ?? 'Kārts').trim().slice(0, LIMITS.cardName) || 'Kārts',
    typeId: c.typeId && nodeIds.has(String(c.typeId)) ? String(c.typeId) : null,
    rarity,
    flavor: String(c.flavor ?? '').slice(0, LIMITS.flavor),
    energy: Math.round(num(c.energy, -60, 60, 0)),
    kind,
    effects: (Array.isArray(c.effects) ? c.effects : [])
      .filter((e) => e && nodeIds.has(String(e.target)))
      .map((e) => ({ target: String(e.target), delta: round(num(e.delta, -3, 3, 0)) }))
      .slice(0, LIMITS.cardEffects),
    rules: (Array.isArray(c.rules) ? c.rules : [])
      .filter((r) => r && (r.metric === 'energy' || nodeIds.has(String(r.metric))))
      .map((r) => ({
        metric: r.metric === 'energy' ? 'energy' : String(r.metric),
        op: r.op === 'gt' ? ('gt' as const) : ('lt' as const),
        value: round(num(r.value, 0, r.metric === 'energy' ? CAP.max : 10, 0)),
        energyMul: round(num(r.energyMul, 0, 5, 1)),
        areaMul: round(num(r.areaMul, 0, 5, 1))
      }))
      .slice(0, LIMITS.cardRules),
    stacks: (Array.isArray(c.stacks) ? c.stacks : [])
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        count: Math.round(num(s.count, 2, 20, 3)),
        days: Math.round(num(s.days, 1, 90, 7)),
        energy: Math.round(num(s.energy, -30, 30, 0)),
        effects: (Array.isArray(s.effects) ? s.effects : [])
          .filter((e) => e && nodeIds.has(String(e.target)))
          .map((e) => ({ target: String(e.target), delta: round(num(e.delta, -3, 3, 0)) }))
          .filter((e) => e.delta !== 0)
          .slice(0, LIMITS.stackEffects),
        label: String(s.label ?? '').trim().slice(0, LIMITS.stackLabel)
      }))
      .filter((s) => s.energy !== 0 || s.effects.length > 0)
      .slice(0, LIMITS.cardStacks)
  };
}

/** Ietilpības līmeņi: līdz 6, `min` 0–10 (0,01), `units` −50…50 (veseli), sakārtoti, bez dublētiem `min`. */
export function sanitizeTiers(t: unknown): CapacityTier[] {
  if (!Array.isArray(t)) return [];
  const byMin = new Map<number, number>();
  for (const x of t) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const min = Number(o.min);
    const units = Number(o.units);
    if (!Number.isFinite(min) || !Number.isFinite(units)) continue;
    byMin.set(round(clamp(min, 0, 10)), Math.round(clamp(units, -CAP.tierUnits, CAP.tierUnits)));
  }
  return [...byMin.entries()]
    .sort((a, b) => a[0] - b[0])
    .slice(0, LIMITS.tiers)
    .map(([min, units]) => ({ min, units }));
}

export function sanitizeNodeEnergy(e: unknown): NodeEnergy | null {
  if (!e || typeof e !== 'object') return null;
  const o = e as Record<string, unknown>;
  const role = o.role as Role;
  if (!EMAX[role]) return null;
  const kinds: string[] = role === 'shield' ? [...KINDS, 'all'] : [...KINDS];
  return {
    role,
    amount: Math.round(num(o.amount, 0, EMAX[role], 0)),
    kind: (kinds.includes(String(o.kind)) ? o.kind : 'fiz') as ShieldKind
  };
}
